import * as React from "react";
import { Box, IconButton, Button, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { KitchenArrivalRow } from "./RallyBoardPopup";
// import { buildReturnPressureSummary, buildReturnPressureTriage } from "./buildReturnPressureSummary";

import { RallyBadge } from "./GameStoryDialog_UPDATED";

import {
  buildGamePointAssignments,
  GamePointAssignmentBucket,
  GamePointAssignmentExample,
} from "./buildGamePointAssignments";

import { ActiveRallyVideo, ActiveRallyVideoHandle } from "./ActiveRallyVideo";
import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { FirefightClip } from "./GameStory_firefights";
import {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import { PounceWindowRow } from "../../shared/types/pounceEtc";
import { RallyShotQualityStrip } from "./GameStory_RallyQualityStrip";

const TRIAGE_TOOLTIP_BG = "rgba(255,255,255,0.97)";
const TRIAGE_TOOLTIP_TEXT = "rgba(0,0,0,0.92)";
const TRIAGE_TOOLTIP_FONT_SIZE = 14;
const TRIAGE_TOOLTIP_BORDER = "rgba(0,0,0,0.14)";

export const TRIAGE_TOOLTIP_POPPER_SX = {
  zIndex: 5000, // 1700,
  "& .MuiTooltip-tooltip": {
    backgroundColor: TRIAGE_TOOLTIP_BG,
    color: TRIAGE_TOOLTIP_TEXT,
    fontSize: TRIAGE_TOOLTIP_FONT_SIZE,
    lineHeight: 1.3,
    fontWeight: 500,
    border: `1px solid ${TRIAGE_TOOLTIP_BORDER}`,
    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
    maxWidth: 320,
    px: 1.1,
    py: 0.8,
  },
  "& .MuiTooltip-arrow": {
    color: TRIAGE_TOOLTIP_BG,
  },
};

const AI_GUIDE_COLOR = "rgba(220,60,60,1)";

const TRIAGE_TEXT_PRIMARY = "rgba(0,0,0,0.88)";
const TRIAGE_TEXT_SECONDARY = "rgba(0,0,0,0.64)";
const TRIAGE_BORDER = "rgba(0,0,0,0.12)";
const TRIAGE_BORDER_STRONG = "rgba(0,0,0,0.18)";
const TRIAGE_BG = "rgba(0,0,0,0.02)";
const TRIAGE_BG_SELECTED = "rgba(0,0,0,0.05)";
const TRIAGE_INSET = "inset 0 0 0 1px rgba(0,0,0,0.04)";
const TRIAGE_SURFACE = "white";
const TRIAGE_SUBTLE_SURFACE = "rgba(0,0,0,0.012)";
const TRIAGE_BLUE_BORDER = "rgba(95,165,255,0.55)";
const TRIAGE_BLUE_BG = "rgba(95,165,255,0.12)";
const TRIAGE_GREEN_BORDER = "rgba(120,225,160,0.55)";
const TRIAGE_GREEN_BG = "rgba(120,225,160,0.12)";

const TRIAGE_BORDER_SOFT = "rgba(0,0,0,0.10)";
const TRIAGE_BORDER_HOVER = "rgba(0,0,0,0.20)";
const TRIAGE_BORDER_HOVER_STRONG = "rgba(0,0,0,0.28)";

const TRIAGE_TEXT_MUTED = "rgba(0,0,0,0.54)";
const TRIAGE_TEXT_SUBTLE = "rgba(0,0,0,0.68)";
const TRIAGE_LOST = "rgba(185,110,50,0.95)";
const TRIAGE_WON = "rgba(28,120,62,0.96)";

const TRIAGE_AVATAR_WRAPPER_BG = "transparent";
const TRIAGE_PANEL_BG = TRIAGE_BG;
const TRIAGE_PANEL_BG_SELECTED = TRIAGE_BG_SELECTED;

const TRIAGE_CONTROL_BORDER = "rgba(0,0,0,0.16)";
const TRIAGE_CONTROL_BG = "rgba(0,0,0,0.03)";
const TRIAGE_CONTROL_BG_HOVER = "rgba(0,0,0,0.05)";
const TRIAGE_CONTROL_TEXT = "rgba(0,0,0,0.62)";

const TRIAGE_HANDLE = "rgba(0,0,0,0.22)";

const TRIAGE_BUCKET_LABELS = {
  gamePointsLost: "Points Allowed",
  gamePointsWon: "Points Scored",
  opportunitiesLost: "Serve Chances Lost",
  opponentOpportunitiesStymied: "Opponent Serve Chances Denied",
  opponentOpportunitiesStymiedShort: "Opp Serve Denied",
} as const;

function getTriageBucketLabel(
  bucketKey: GamePointAssignmentBucket["bucketKey"],
  fallbackTitle: string,
): string {
  switch (bucketKey) {
    case "game_points_lost":
      return TRIAGE_BUCKET_LABELS.gamePointsLost;
    case "game_points_won":
      return TRIAGE_BUCKET_LABELS.gamePointsWon;
    case "opportunities_lost":
      return TRIAGE_BUCKET_LABELS.opportunitiesLost;
    case "opponent_opportunities_stymied":
      return TRIAGE_BUCKET_LABELS.opponentOpportunitiesStymied;
    default:
      return fallbackTitle;
  }
}

export type TriagePlayerId = 0 | 1 | 2 | 3;
export const TRIAGE_PLAYER_IDS: TriagePlayerId[] = [0, 1, 2, 3];

export type TriageExample = {
  rallyIndex: number;
  label?: string;
  reasonText?: string;
  score?: number;
};

export type TriageCandidate = {
  id: string;
  methodKey: string;
  playerId: TriagePlayerId;

  title: string;
  body: string;
  score: number;

  bullets: string[];
  examples: TriageExample[];
};

export type TriageBuildResult = {
  methodKey: string;
  byPlayerId: Record<TriagePlayerId, TriageCandidate[]>;
};

const MIN_W = 420;
const MIN_H = 280;
const DEFAULT_W = 1520;
const DEFAULT_H = 820;

function pullBackPoint(args: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  pullBackPx: number;
}) {
  const { startX, startY, endX, endY, pullBackPx } = args;
  const dx = endX - startX;
  const dy = endY - startY;
  const len = Math.hypot(dx, dy);

  if (!Number.isFinite(len) || len < 0.001) {
    return { x: endX, y: endY };
  }

  const ux = dx / len;
  const uy = dy / len;

  return {
    x: endX - ux * pullBackPx,
    y: endY - uy * pullBackPx,
  };
}

function rectToLocal(rect: DOMRect, parentRect: DOMRect) {
  return {
    left: rect.left - parentRect.left,
    top: rect.top - parentRect.top,
    right: rect.right - parentRect.left,
    bottom: rect.bottom - parentRect.top,
    width: rect.width,
    height: rect.height,
    cx: rect.left - parentRect.left + rect.width / 2,
    cy: rect.top - parentRect.top + rect.height / 2,
  };
}

export function GameStoryTriagePopup(props: {
  open: boolean;
  packageId: string;
  rows: KitchenArrivalRow[];
  onClose: () => void;

  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity: RenderPlayerIdentity;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  pounceWindowRows: PounceWindowRow[];
  badgesByRallyIndex: Map<number, RallyBadge[]>;
}) {
  const {
    packageId,
    open,
    rows,
    onClose,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
    firefightsClips,
    firefightRallySet,
    pounceWindowRows,
    badgesByRallyIndex,
  } = props;

  const activeRallyVideoRef = React.useRef<ActiveRallyVideoHandle | null>(null);
  const [triageAiOpen, setTriageAiOpen] = React.useState(false); //true); //false);
  const [triageExportBusy, setTriageExportBusy] = React.useState(false);

  const onTriageToggleAi = React.useCallback(() => {
    //activeRallyVideoRef.current?.toggleAiPanel();

    setTriageAiOpen((prev) => !prev);
    //   console.error("onTriageToggleAi", triageAiOpen);

    //   setTriageAiOpen((prev) => {
    //     console.error("onTriageToggleAi setting to true");
    //     return true;
    // }); // !prev);
    //     activeRallyVideoRef.current?.toggleAiPanel();
    // setTriageAiOpen((prev) => {
    //   const next = !prev;
    //   activeRallyVideoRef.current?.toggleAiPanel();
    //   return next;
    // });
  }, []);

  //   React.useEffect(() => {
  //   if (!activeRallyVideoRef.current) return;
  //   activeRallyVideoRef.current.toggleAiPanel?.();
  // }, [triageAiOpen]);

  const onTriageExportHtml = React.useCallback(async () => {
    if (!activeRallyVideoRef.current || triageExportBusy) return;
    try {
      setTriageExportBusy(true);
      await activeRallyVideoRef.current.toggleExportHtml();
    } finally {
      setTriageExportBusy(false);
    }
  }, [triageExportBusy]);

  const popupRootRef = React.useRef<HTMLDivElement | null>(null);
  const aiHelperRef = React.useRef<HTMLSpanElement | null>(null);
  const [aiButtonAnchorEl, setAiButtonAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [showAiGuide, setShowAiGuide] = React.useState(false);
  const [aiGuideVersion, setAiGuideVersion] = React.useState(0);

  const [playbackShotNumber, setPlaybackShotNumber] = React.useState<
    number | null
  >(null);

  const [showMethodology, setShowMethodology] = React.useState(false);
  const [selectedPlayerId, setSelectedPlayerId] =
    React.useState<TriagePlayerId>(0);

  const [selectedExampleIndexByBucketKey, setSelectedExampleIndexByBucketKey] =
    React.useState<Record<string, number>>({});
  const [logicExample, setLogicExample] =
    React.useState<GamePointAssignmentExample | null>(null);

  const [frame, setFrame] = React.useState({
    x: 30,
    y: 30,
    w: DEFAULT_W,
    h: DEFAULT_H,
  });

  React.useEffect(() => {
    if (!open) return;
    setFrame((prev) => ({
      ...prev,
      w: DEFAULT_W,
      h: DEFAULT_H,
    }));
  }, [open]);

  const dragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const resizeRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originW: number;
    originH: number;
  } | null>(null);

  const onHeaderPointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("[data-no-drag='true']")) return;

      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originX: frame.x,
        originY: frame.y,
      };

      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      e.preventDefault();
    },
    [frame.x, frame.y],
  );

  const onHeaderPointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const s = dragRef.current;
      if (!s || s.pointerId !== e.pointerId) return;

      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;

      setFrame((prev) => ({
        ...prev,
        x: Math.max(8, s.originX + dx),
        y: Math.max(8, s.originY + dy),
      }));
    },
    [],
  );

  const onHeaderPointerUp = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const s = dragRef.current;
      if (!s || s.pointerId !== e.pointerId) return;
      dragRef.current = null;
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    },
    [],
  );

  const onResizePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      resizeRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originW: frame.w,
        originH: frame.h,
      };

      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      e.preventDefault();
      e.stopPropagation();
    },
    [frame.w, frame.h],
  );

  const onResizePointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const s = resizeRef.current;
      if (!s || s.pointerId !== e.pointerId) return;

      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;

      setFrame((prev) => ({
        ...prev,
        w: Math.max(MIN_W, s.originW + dx),
        h: Math.max(MIN_H, s.originH + dy),
      }));
    },
    [],
  );

  const onResizePointerUp = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const s = resizeRef.current;
      if (!s || s.pointerId !== e.pointerId) return;
      resizeRef.current = null;
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    },
    [],
  );

  const gamePointAssignments = React.useMemo(() => {
    return buildGamePointAssignments({
      rows,
      badgesByRallyIndex,
      pounceWindowRows,
      // insightsJson,
      // cvJson,
      //   targetScore: 11,
      //   winBy: 2,
      //   onlyIncludeGameEndingRallies: false,
    });
  }, [rows, badgesByRallyIndex, pounceWindowRows]);

  const selectedPlayerBuckets = React.useMemo(() => {
    return gamePointAssignments.byPlayerId[selectedPlayerId];
  }, [gamePointAssignments, selectedPlayerId]);

  // const triageBuckets = React.useMemo(() => {
  //   const out: GamePointAssignmentBucket[] = [];
  //   if (selectedPlayerBuckets.gamePointsLost)
  //     out.push(selectedPlayerBuckets.gamePointsLost);
  //   if (selectedPlayerBuckets.gamePointsWon)
  //     out.push(selectedPlayerBuckets.gamePointsWon);
  //   return out;
  // }, [selectedPlayerBuckets]);

  const triageBuckets = React.useMemo(() => {
    const out: GamePointAssignmentBucket[] = [];

    if (selectedPlayerBuckets.gamePointsLost) {
      out.push(selectedPlayerBuckets.gamePointsLost);
    }

    if (selectedPlayerBuckets.gamePointsWon) {
      out.push(selectedPlayerBuckets.gamePointsWon);
    }

    if (selectedPlayerBuckets.opportunitiesLost) {
      out.push(selectedPlayerBuckets.opportunitiesLost);
    }

    if (selectedPlayerBuckets.opponentOpportunitiesStymied) {
      out.push(selectedPlayerBuckets.opponentOpportunitiesStymied);
    }

    return out;
  }, [selectedPlayerBuckets]);

  const [selectedBucketId, setSelectedBucketId] = React.useState<string | null>(
    null,
  );

  React.useEffect(() => {
    if (!open) return;
    setSelectedBucketId((prev) => {
      if (prev && triageBuckets.some((b) => b.bucketKey === prev)) return prev;
      return triageBuckets[0]?.bucketKey ?? null;
    });
  }, [open, triageBuckets]);

  React.useEffect(() => {
    if (!open) setLogicExample(null);
  }, [open]);

  React.useEffect(() => {
    setLogicExample(null);
  }, [selectedPlayerId]);

  const [seedNonce, setSeedNonce] = React.useState(0);
  const selectedBucket =
    triageBuckets.find((b) => b.bucketKey === selectedBucketId) ??
    triageBuckets[0] ??
    null;

  const selectedExampleIndex =
    selectedBucket == null
      ? 0
      : (selectedExampleIndexByBucketKey[selectedBucket.bucketKey] ?? 0);

  const selectedExample: GamePointAssignmentExample | null =
    selectedBucket?.examples?.[selectedExampleIndex] ??
    selectedBucket?.examples?.[0] ??
    null;

  React.useEffect(() => {
    setTriageAiOpen(false);
  }, [selectedExample?.rallyIndex]);

  const selectedRow = React.useMemo(() => {
    if (!selectedExample) return null;
    return rows[selectedExample.rallyIndex] ?? null;
  }, [rows, selectedExample]);

  React.useEffect(() => {
    if (!selectedExample) return;
    setSeedNonce((n) => n + 1);
  }, [selectedBucket?.bucketKey, selectedExample?.rallyIndex]);

  React.useEffect(() => {
    setPlaybackShotNumber(selectedExample?.focusShotNumber ?? null);
  }, [selectedExample?.rallyIndex, selectedExample?.focusShotNumber]);

  const aiGuideGeometry = React.useMemo(() => {
    if (!showAiGuide) return null;
    if (!popupRootRef.current || !aiHelperRef.current || !aiButtonAnchorEl)
      return null;

    const popupRect = popupRootRef.current.getBoundingClientRect();
    const fromRect = aiHelperRef.current.getBoundingClientRect();
    const toRect = aiButtonAnchorEl.getBoundingClientRect();

    const from = rectToLocal(fromRect, popupRect);
    const to = rectToLocal(toRect, popupRect);

    const rawStartX = from.right + 4; // - 2;
    const rawStartY = from.cy + 4; //  - 1;
    // const rawEndX = to.left + to.width * 0.18;
    // const rawEndY = to.top + to.height * 0.18;

    const rawEndX = to.cx;
    const rawEndY = to.top + 6;

    const pulled = pullBackPoint({
      startX: rawStartX,
      startY: rawStartY,
      endX: rawEndX,
      endY: rawEndY,
      pullBackPx: 20,
    });

    return {
      startX: rawStartX,
      startY: rawStartY,
      endX: pulled.x,
      endY: pulled.y,
      rawEndX,
      rawEndY,
    };
  }, [showAiGuide, aiButtonAnchorEl, aiGuideVersion]);

  React.useLayoutEffect(() => {
    if (!showAiGuide) return;
    setAiGuideVersion((v) => v + 1);
  }, [
    showAiGuide,
    frame.x,
    frame.y,
    frame.w,
    frame.h,
    selectedExample?.rallyIndex,
    selectedBucket?.bucketKey,
  ]);

  if (!open) return null;

  return (
    <>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1590,
          background: "rgba(0,0,0,0.42)",
          // backdropFilter: "blur(2px)",
        }}
      />

      <Box
        ref={popupRootRef}
        sx={{
          position: "fixed",
          left: frame.x,
          top: frame.y,
          width: frame.w,
          height: frame.h,
          zIndex: 1600,
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          // bgcolor: "rgb(12,12,12)",
          // color: "white",
          bgcolor: TRIAGE_SURFACE,
          color: TRIAGE_TEXT_PRIMARY,
          border: `1px solid ${TRIAGE_BORDER}`,
          boxShadow: "0 28px 70px rgba(0,0,0,0.62)",
        }}
      >
        <Box
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          sx={{
            px: 1.3,
            pt: 1.1,
            pb: 1.0,
            // border: "1px solid rgba(255,230,120,0.18)",
            // background:
            //   "linear-gradient(to bottom, rgba(255,225,110,0.08), rgba(255,225,110,0.02) 45%, rgba(255,255,255,0.00))",

            borderBottom: `1px solid ${TRIAGE_BORDER}`,
            // background: "rgba(255,255,255,0.015)",
            background: TRIAGE_SURFACE,

            cursor: "move",
            userSelect: "none",
            touchAction: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 0.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: 21,
                  fontWeight: 900,
                  lineHeight: 1.05,
                  // color: "rgba(255,228,110,0.98)",
                  color: TRIAGE_TEXT_PRIMARY,
                }}
              >
                3-Minute Triage for Reviewing Rallies
                <Box
                  data-no-drag="true"
                  sx={{
                    mt: "4px",
                    fontSize: 14,
                    lineHeight: 1.35,
                    // color: "rgba(255,255,255,0.9)",
                    color: TRIAGE_TEXT_SECONDARY,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.9,
                    marginLeft: "12px",
                    fontWeight: 500,
                  }}
                >
                  {/* Highlights starting points for reviewing game-point rallies. */}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowMethodology((v) => !v)}
                    sx={{
                      textTransform: "none",
                      fontSize: 11.5,
                      px: 0.8,
                      py: 0.15,

                      // borderColor: "rgba(0,0,0,0.16)",
                      // background: "rgba(0,0,0,0.03)",
                      // color: "rgba(0,0,0,0.62)",
                      minHeight: 28,
                      // borderRadius: "8px",
                      // "&:hover": {
                      //   borderColor: "rgba(0,0,0,0.24)",
                      //   background: "rgba(0,0,0,0.05)",
                      // },

                      borderColor: TRIAGE_CONTROL_BORDER,
                      background: TRIAGE_CONTROL_BG,
                      color: TRIAGE_CONTROL_TEXT,
                      "&:hover": {
                        borderColor: TRIAGE_BORDER_HOVER,
                        background: TRIAGE_CONTROL_BG_HOVER,
                      },
                    }}
                  >
                    {showMethodology ? "Hide info" : "More info"}
                  </Button>
                  {/* <Box
                    component="span"
                    ref={aiHelperRef}
                    onMouseEnter={() => {
                      setShowAiGuide(true);
                      setAiGuideVersion((v) => v + 1);
                    }}
                    onMouseLeave={() => {
                      setShowAiGuide(false);
                    }}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "rgba(70,110,205,0.92)",
                      cursor: "pointer",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                      whiteSpace: "nowrap",
                      transition: "color 120ms ease",
                      "&:hover": {
                        color: "rgba(45,90,200,0.98)",
                      },
                    }}
                  >
                    Use AI to dig deeper on any part of any rally.
                  </Box> */}

                  <Tooltip
                    title={
                      triageAiOpen
                        ? "Hide the AI panel for this rally."
                        : "Open the AI panel for this rally to ask follow-up questions about what happened and what to review."
                    }
                    arrow
                    placement="bottom"
                    slotProps={{
                      popper: {
                        sx: TRIAGE_TOOLTIP_POPPER_SX,
                      },
                    }}
                    // slotProps={{
                    //   popper: {
                    //     sx: {
                    //       zIndex: 1700,
                    //     },
                    //   },
                    // }}
                  >
                    <span>
                      <Button
                        size="small"
                        variant={triageAiOpen ? "contained" : "outlined"}
                        onClick={onTriageToggleAi}
                        sx={{
                          textTransform: "none",
                          fontSize: 11.5,
                          px: 0.9,
                          py: 0.15,
                          minHeight: 28,
                          borderColor: TRIAGE_CONTROL_BORDER,
                          background: triageAiOpen
                            ? TRIAGE_BLUE_BG
                            : TRIAGE_CONTROL_BG,
                          color: TRIAGE_CONTROL_TEXT,
                          "&:hover": {
                            borderColor: TRIAGE_BORDER_HOVER,
                            background: triageAiOpen
                              ? TRIAGE_BLUE_BG
                              : TRIAGE_CONTROL_BG_HOVER,
                          },
                        }}
                      >
                        {triageAiOpen
                          ? "Close AI Panel for Rally"
                          : "Open AI Panel for Rally"}
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={
                      !selectedRow
                        ? "Select a rally to export it as a standalone HTML clip."
                        : triageExportBusy
                          ? "Exporting this rally as a standalone HTML file you can open with a browser..."
                          : "Export this rally as a standalone HTML file you can open or share outside the app."
                    }
                    arrow
                    placement="bottom"
                    slotProps={{
                      popper: {
                        sx: TRIAGE_TOOLTIP_POPPER_SX,
                      },
                    }}
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={onTriageExportHtml}
                        disabled={!selectedRow || triageExportBusy}
                        sx={{
                          textTransform: "none",
                          fontSize: 11.5,
                          px: 0.9,
                          py: 0.15,
                          minHeight: 28,
                          borderColor: TRIAGE_CONTROL_BORDER,
                          background: TRIAGE_CONTROL_BG,
                          color: TRIAGE_CONTROL_TEXT,
                          "&:hover": {
                            borderColor: TRIAGE_BORDER_HOVER,
                            background: TRIAGE_CONTROL_BG_HOVER,
                          },
                        }}
                      >
                        {triageExportBusy ? "Exporting..." : "Export to Html"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            <Box data-no-drag="true">
              <IconButton
                onClick={onClose}
                size="small"
                // sx={{ color: "rgba(255,255,255,0.76)" }}
                sx={{ color: TRIAGE_TEXT_SUBTLE }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {showMethodology && (
            <Box
              sx={{
                mt: 0.6,
                px: 0.4,
                py: 0.6,
                fontSize: 14,
                lineHeight: 1.45,
                borderTop: `1px solid ${TRIAGE_BORDER}`,
                background: TRIAGE_SUBTLE_SURFACE,
                color: TRIAGE_TEXT_SECONDARY,
              }}
            >
              <Box sx={{ fontWeight: 600, mb: 0.55 }}>
                This is quick triage - not exact causality.
              </Box>

              <Box sx={{ mb: 0.65 }}>
                The goal is to help you quickly prioritize a few rallies to
                inspect more closely, which can be starting places for the
                deeper analyses that are available in the app.
              </Box>

              <Box sx={{ mb: 0.65 }}>
                The heuristic for assigning initial responsibility/credit is
                based on:
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.35,
                  pl: 0.7,
                }}
              >
                <Box>
                  • Immediate misses are usually assigned to that hitter.
                </Box>
                <Box>
                  • Popup-exploited shots are treated as strong early review
                  candidates.
                </Box>
                {/* <Box>• Setup shots may be highlighted over the final pressured miss.</Box> */}
                <Box>
                  • Winning closeouts usually credit the terminal hitter.
                </Box>
                <Box>
                  • Otherwise, responsibility usually falls to the last shot by
                  the losing team.
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            px: "4px",
            py: "4px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 0.9,
              pt: 0.85,
              pb: 0.75,
              //borderBottom: "1px solid rgba(255,255,255,0.08)",
              borderBottom: `1px solid ${TRIAGE_BORDER}`,

              display: "flex",
              flexDirection: "column",
              gap: 0.35,
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.65 }}>
              {TRIAGE_PLAYER_IDS.map((playerId) => {
                const isSelected = selectedPlayerId === playerId;

                const team = playerId === 0 || playerId === 1 ? 0 : 1;

                const playerBuckets = gamePointAssignments.byPlayerId[playerId];
                const lostCount =
                  playerBuckets.gamePointsLost?.examples.length ?? 0;
                const wonCount =
                  playerBuckets.gamePointsWon?.examples.length ?? 0;

                const oppLostCount =
                  playerBuckets.opportunitiesLost?.examples.length ?? 0;

                const stymiedCount =
                  playerBuckets.opponentOpportunitiesStymied?.examples.length ??
                  0;

                return (
                  <Button
                    key={playerId}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedPlayerId(playerId);
                      setSelectedBucketId(null);
                      setSelectedExampleIndexByBucketKey({});
                    }}
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      px: 0.7,
                      py: 0.35,
                      // borderColor: isSelected
                      //   ? "rgba(255,226,115,0.50)"
                      //   : "rgba(255,255,255,0.16)",
                      // background: isSelected
                      //   ? "rgba(255,226,115,0.06)"
                      //   : "transparent",
                      // color: "rgba(255,255,255,0.86)",
                      // "&:hover": {
                      //   borderColor: isSelected
                      //     ? "rgba(255,226,115,0.50)"
                      //     : "rgba(255,226,115,1)", //red", //"rgba(255,255,255,0.16)",
                      // },

                      borderColor: isSelected
                        ? team === 0
                          ? TRIAGE_BLUE_BORDER
                          : TRIAGE_GREEN_BORDER
                        : TRIAGE_BORDER, // "rgba(0,0,0,0.12)",
                      // : "rgba(255,255,255,0.16)",
                      background: isSelected
                        ? team === 0
                          ? TRIAGE_BLUE_BG
                          : TRIAGE_GREEN_BG
                        : TRIAGE_AVATAR_WRAPPER_BG, //  "transparent",
                      // color: "rgba(255,255,255,0.86)",
                      color: TRIAGE_TEXT_PRIMARY,
                      "&:hover": {
                        borderColor: isSelected
                          ? team === 0
                            ? TRIAGE_BLUE_BORDER
                            : TRIAGE_GREEN_BORDER
                          : TRIAGE_BORDER_HOVER, // "rgba(0,0,0,0.44)",
                        //: "rgba(255,255,255,0.24)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 24,
                        background: TRIAGE_AVATAR_WRAPPER_BG, //"transparent", // "white",
                        borderRadius: "4px",
                        px: 0, //"4px",
                        py: 0, // "4px",
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          opacity: isSelected ? 1 : 0.7,
                        }}
                      >
                        {renderPlayerIdentity({
                          playerId,
                          size: 44,
                          fontSize: 12,
                        })}
                      </Box>

                      <Box sx={{ width: 10, flexShrink: 0 }} />

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          minWidth: 220,
                          lineHeight: 1.05,
                          fontWeight: isSelected ? 900 : 400,
                        }}
                      >
                        <Box
                          sx={{
                            opacity: isSelected ? 1 : 0.6,
                            display: "flex",
                            gap: 0.6,
                          }}
                        >
                          <Box
                            sx={{
                              width: 130,
                              whiteSpace: "nowrap",
                              flexShrink:0,
                              color: TRIAGE_LOST,
                            }}
                          >
                            {TRIAGE_BUCKET_LABELS.gamePointsLost}
                          </Box>
                          <Box
                            sx={{
                              color: TRIAGE_LOST,
                            }}
                          >
                            {lostCount}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            opacity: isSelected ? 1 : 0.6,
                            display: "flex",
                            gap: 0.6,
                          }}
                        >
                          <Box
                            sx={{
                              width: 130,
                              whiteSpace: "nowrap",
                              flexShrink:0,
                              color: TRIAGE_WON,
                            }}
                          >
                            {TRIAGE_BUCKET_LABELS.gamePointsWon}
                          </Box>
                          <Box sx={{ color: TRIAGE_WON }}>{wonCount}</Box>
                        </Box>

                        <Box
                          sx={{
                            opacity: isSelected ? 1 : 0.6,
                            display: "flex",
                            gap: 0.6,
                          }}
                        >
                          <Box sx={{ 
                              width: 130,
                              whiteSpace: "nowrap",
                              flexShrink:0,

                            color: TRIAGE_LOST 
                            }}>
                            {TRIAGE_BUCKET_LABELS.opportunitiesLost}
                          </Box>
                          <Box sx={{ color: TRIAGE_LOST }}>{oppLostCount}</Box>
                        </Box>

                        <Box
                          sx={{
                            opacity: isSelected ? 1 : 0.6,
                            display: "flex",
                            gap: 0.6,
                          }}
                        >
                          <Box sx={{ 
                              width: 130,
                              whiteSpace: "nowrap",
                              flexShrink:0,
                            color: TRIAGE_WON 
                            }}>
                            {
                              TRIAGE_BUCKET_LABELS.opponentOpportunitiesStymiedShort
                            }
                          </Box>
                          <Box sx={{ color: TRIAGE_WON }}>{stymiedCount}</Box>
                        </Box>

                      </Box>
                    </Box>
                  </Button>
                );
              })}
            </Box>
          </Box>

          {/* start triageBuckets conditional */}
          {triageBuckets.length === 0 ? (
            <Box
              sx={{
                px: 1.1,
                py: 2.0,
                borderRadius: 1.5,
                border: `1px solid ${TRIAGE_BORDER}`,
                background: TRIAGE_SUBTLE_SURFACE,
                fontSize: 14,
                fontWeight: 700,
                // color: "rgba(255,255,255,0.70)",
                color: TRIAGE_TEXT_SECONDARY,
              }}
            >
              {/* No game-point assignments landed on this player yet. */}
              No triage assignments landed on this player yet.
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  gap: 0,

                  border: `1px solid ${TRIAGE_BORDER}`,
                  bgcolor: TRIAGE_SURFACE,

                  borderRadius: 1.5,
                  overflow: "hidden",
                  color: TRIAGE_TEXT_SECONDARY,
                }}
              >
                <Box
                  sx={{
                    width: 440,
                    minWidth: 440,
                    maxWidth: 500,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    borderRight: `1px solid ${TRIAGE_BORDER}`,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      // maxHeight: 430,
                      overflowY: "auto",
                      px: 0.9,
                      py: 0.9,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0, // 1.25,
                    }}
                  >
                    {triageBuckets.map((bucket) => {
                      const isSelected = selectedBucketId === bucket.bucketKey;
                      const selectedExampleIdxForBucket =
                        selectedExampleIndexByBucketKey[bucket.bucketKey] ?? 0;

                      return (
                        <Box
                          key={bucket.bucketKey}
                          sx={{
                            p: 1.05,
                            borderRadius: 1.35,

                            border: isSelected
                              ? `1px solid ${TRIAGE_BORDER_STRONG}`
                              : `1px solid ${TRIAGE_BORDER}`,
                            boxShadow: isSelected ? TRIAGE_INSET : "none",
                            background: isSelected
                              ? TRIAGE_PANEL_BG_SELECTED
                              : TRIAGE_PANEL_BG,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 0.9,
                            }}
                          >
                            {/* LEFT SIDE: avatar + title + body */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.6,
                                minWidth: 0,
                              }}
                            >
                              {/* LEFT AVATAR (existing one stays) */}
                              <Box
                                sx={{
                                  background: TRIAGE_AVATAR_WRAPPER_BG, // "transparent", // "white",
                                  borderRadius: "4px",
                                  // marginTop: "4px",
                                  marginRight: "4px",
                                  px: 0.35,
                                  py: 0.25,
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {renderPlayerIdentity({
                                  playerId: selectedPlayerId,
                                  size: 40,
                                  fontSize: 10,
                                })}
                              </Box>

                              {/* TITLE + BODY */}
                              <Box sx={{ minWidth: 0 }}>
                                <Box
                                  sx={{
                                    fontSize: 14.5,
                                    fontWeight: 900,
                                    lineHeight: 1.08,
                                    // color: "rgba(255,232,125,0.98)",
                                    color: TRIAGE_TEXT_PRIMARY,
                                  }}
                                >
                                  {getTriageBucketLabel(
                                    bucket.bucketKey,
                                    bucket.title,
                                  )}{" "}
                                  ({bucket.examples.length})
                                </Box>

                                {/* <Box
                                  sx={{
                                    display: "none",
                                    mt: 0.4,
                                    fontSize: 12.5,
                                    lineHeight: 1.35,
                                    // color: "rgba(255,255,255,0.76)",
                                    color: TRIAGE_TEXT_PRIMARY,
                                  }}
                                >
                                  {bucket.body}
                                </Box> */}
                              </Box>
                            </Box>

                            {/* RIGHT AVATAR (new reinforcement anchor) */}
                            <Box
                              sx={{
                                background: "transparent", // "white",
                                borderRadius: "4px",
                                // marginTop: "4px",

                                px: 0.35,
                                py: 0.25,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                //opacity: 0.85,
                              }}
                            >
                              {renderPlayerIdentity({
                                playerId: selectedPlayerId,
                                size: 40, // 34,
                                fontSize: 10,
                              })}
                            </Box>
                          </Box>

                          {bucket.examples.length > 0 ? (
                            <Box
                              sx={{
                                mt: 0.85,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.55,
                              }}
                            >
                              {bucket.examples.map((ex, idx) => {
                                const exampleSelected =
                                  isSelected &&
                                  selectedExampleIdxForBucket === idx;

                                return (
                                  <Box
                                    key={`${bucket.bucketKey}-${ex.rallyIndex}-${idx}`}
                                    sx={{ display: "flex", minWidth: 0 }}
                                  >
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                        setSelectedBucketId(bucket.bucketKey);
                                        setSelectedExampleIndexByBucketKey(
                                          (prev) => ({
                                            ...prev,
                                            [bucket.bucketKey]: idx,
                                          }),
                                        );
                                      }}
                                      sx={{
                                        flex: 1,
                                        textTransform: "none",
                                        fontWeight: 800,
                                        fontSize: 11.5,
                                        minWidth: 0,
                                        px: 0.8,
                                        py: 0.3,
                                        color: TRIAGE_TEXT_SECONDARY,

                                        borderColor: exampleSelected
                                          ? TRIAGE_BORDER_STRONG
                                          : TRIAGE_BORDER,
                                        background: exampleSelected
                                          ? TRIAGE_BG_SELECTED
                                          : TRIAGE_AVATAR_WRAPPER_BG,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {exampleSelected ? "Showing " : ""}
                                      {ex.label ??
                                        `Rally ${ex.rallyIndex + 1}`}
                                      {" (" +
                                        (rows[ex.rallyIndex].shotNumberOverlays
                                          ?.length ?? 0) +
                                        " shots)"}
                                    </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setLogicExample(ex);
                                    }}
                                    sx={{
                                      ml: 0.55,
                                      textTransform: "none",
                                      fontWeight: 800,
                                      fontSize: 11,
                                      minWidth: 54,
                                      px: 0.65,
                                      py: 0.3,
                                      color: TRIAGE_CONTROL_TEXT,
                                      borderColor: TRIAGE_CONTROL_BORDER,
                                      background: TRIAGE_CONTROL_BG,
                                      whiteSpace: "nowrap",
                                      "&:hover": {
                                        borderColor: TRIAGE_BORDER_HOVER,
                                        background: TRIAGE_CONTROL_BG_HOVER,
                                      },
                                    }}
                                  >
                                    Logic
                                  </Button>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      px: 1.05,
                      py: 0.9,
                      borderBottom: `1px solid ${TRIAGE_BORDER}`,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {selectedBucket ? (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.75,
                            minWidth: 0,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box
                            sx={{
                              background: "white",
                              borderRadius: "4px",
                              px: 0.4,
                              py: 0.35,
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {renderPlayerIdentity({
                              playerId: selectedPlayerId,
                              size: 40,
                              fontSize: 11,
                            })}
                          </Box>

                          <Box
                            sx={{
                              fontSize: 15,
                              fontWeight: 900,
                              lineHeight: 1.08,
                              color: TRIAGE_TEXT_PRIMARY,
                              minWidth: 0,
                            }}
                          >
                            {/* {selectedBucket.title === "Game Points Lost"
                              ? "Game Point Lost"
                              : selectedBucket.title === "Game Points Won"
                                ? "Game Point Won"
                                : selectedBucket.title} */}

                            {getTriageBucketLabel(
                              selectedBucket.bucketKey,
                              selectedBucket.title,
                            )}
                          </Box>

                          <Box sx={{ opacity: 1 }}>•</Box>

                          <Box
                            sx={{
                              fontSize: 15,
                              fontWeight: 900,
                              lineHeight: 1.08,
                              color: TRIAGE_TEXT_PRIMARY,
                              minWidth: 0,
                            }}
                          >
                            {selectedExample
                              ? `Rally ${selectedExample.rallyIndex + 1} (${rows[selectedExample.rallyIndex].shotNumberOverlays?.length ?? 0} shots)`
                              : "No rally selected"}
                          </Box>
                        </Box>
                      </>
                    ) : (
                      <Box
                        sx={{
                          fontSize: 13,
                          lineHeight: 1.35,
                          color: TRIAGE_TEXT_SECONDARY,
                        }}
                      >
                        No item selected.
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ pl: 0.75, pr: 0.2, pt: 0.3 }}>
                    <CondensedRallyTimeline_ContextOnly
                      activeRally={selectedRow}
                      rows={rows}
                      barsAreaHeightPx={50}
                      colW={12}
                      showLegend={false}
                      showRallyIndex={true}
                      showWinnerAndServe={true}
                      firefightRallySet={firefightRallySet}
                    />
                  </Box>

                  <Box
                    sx={{
                      borderTop: `1px solid ${TRIAGE_BORDER_SOFT}`,

                      pt: 0.75,
                      pb: 0.15,
                      px: 0.1,
                    }}
                  >
                    <RallyShotQualityStrip
                      row={selectedRow}
                      renderPlayerIdentity={renderPlayerIdentity}
                      currentShotNumber={playbackShotNumber}
                      focusShotNumber={null}
                      selectedPlayerId={selectedPlayerId}
                      height={80}
                    />
                  </Box>

                  {!triageAiOpen && (
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 360,
                        position: "relative",
                        overflow: "hidden",
                        bgcolor: "black",
                        borderTop: `1px solid ${TRIAGE_BORDER}`,
                      }}
                    >
                      {selectedRow && selectedBucket ? (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              minHeight: 0,
                              height: "100%",
                            }}
                          >
                            <ActiveRallyVideo
                              //onAiOpenChange={setTriageAiOpen}
                              aiOpenViaParent={triageAiOpen}
                              ref={activeRallyVideoRef}
                              highlightAiEntryPoint={showAiGuide}
                              onAiButtonAnchorReady={setAiButtonAnchorEl}
                              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                              renderPlayerIdentity={renderPlayerIdentity}
                              aiContextLabel="3-Minute Triage"
                              focusedFilter={null}
                              packageId={packageId}
                              row={selectedRow}
                              rows={rows}
                              activeShotNumber={
                                selectedExample?.focusShotNumber ?? null
                              }
                              activeShotExtraText={undefined}
                              shotOverlayPersistentExtraText={undefined}
                              showCondensedTimeline={false}
                              fontColor="white"
                              controlsPlacement="left"
                              firefightsClips={firefightsClips}
                              firefightRallySet={firefightRallySet}
                              showRateControls={true}
                              showTitle={false}
                              showScoreEtc={false}
                              showAi={true}
                              aiDrawerMode="split"
                              showLoopScrubber={true}
                              defaultLoopUiOpen={false}
                              showPopoutButton={true}
                              isInFullPopup={false}
                              seedPlayback={{
                                timeSec: 0,
                                paused: false,
                                nonce: seedNonce,
                              }}
                              onPlaybackShotNumberChange={setPlaybackShotNumber}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: 2,
                            color: "white",
                            opacity: 0.72,
                          }}
                        >
                          No rally selected.
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
          {/* end of triageBuckets conditional */}

          {/* video area always shown */}

          {/* full-width video only when AI is open */}
          {triageAiOpen && (
            <Box
              sx={{
                flex: 1,
                minHeight: 360,
                position: "relative",
                overflow: "hidden",
                bgcolor: "black",
                borderTop: `1px solid ${TRIAGE_BORDER}`,
              }}
            >
              {selectedRow && selectedBucket ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: 0,
                      height: "100%",
                    }}
                  >
                    <ActiveRallyVideo
                      //onAiOpenChange={setTriageAiOpen}
                      aiOpenViaParent={triageAiOpen}
                      ref={activeRallyVideoRef}
                      highlightAiEntryPoint={showAiGuide}
                      onAiButtonAnchorReady={setAiButtonAnchorEl}
                      insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                      renderPlayerIdentity={renderPlayerIdentity}
                      aiContextLabel="3-Minute Triage"
                      focusedFilter={null}
                      packageId={packageId}
                      row={selectedRow}
                      rows={rows}
                      activeShotNumber={
                        selectedExample?.focusShotNumber ?? null
                      }
                      activeShotExtraText={undefined}
                      shotOverlayPersistentExtraText={undefined}
                      showCondensedTimeline={false}
                      fontColor="white"
                      controlsPlacement="left"
                      firefightsClips={firefightsClips}
                      firefightRallySet={firefightRallySet}
                      showRateControls={true}
                      showTitle={false}
                      showScoreEtc={false}
                      showAi={true}
                      aiDrawerMode="split"
                      showLoopScrubber={true}
                      defaultLoopUiOpen={false}
                      showPopoutButton={true}
                      isInFullPopup={false}
                      seedPlayback={{
                        timeSec: 0,
                        paused: false,
                        nonce: seedNonce,
                      }}
                      onPlaybackShotNumberChange={setPlaybackShotNumber}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    color: "white",
                    opacity: 0.72,
                  }}
                >
                  No rally selected.
                </Box>
              )}
            </Box>
          )}
          {/* end of video area always shown */}

          {logicExample ? (
            <Box
              data-no-drag="true"
              sx={{
                position: "absolute",
                left: 22,
                right: 22,
                bottom: 24,
                maxHeight: "min(460px, calc(100% - 76px))",
                zIndex: 1400,
                border: `1px solid ${TRIAGE_BORDER_STRONG}`,
                borderRadius: 1.5,
                background: TRIAGE_SURFACE,
                boxShadow: "0 18px 52px rgba(0,0,0,0.34)",
                color: TRIAGE_TEXT_PRIMARY,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 1.25,
                  py: 0.85,
                  borderBottom: `1px solid ${TRIAGE_BORDER}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ fontSize: 14.5, fontWeight: 900, minWidth: 0 }}>
                  Logic •{" "}
                  {getTriageBucketLabel(
                    logicExample.bucketKey,
                    logicExample.bucketKey,
                  )}{" "}
                  • Rally {logicExample.rallyIndex + 1}
                </Box>
                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  onClick={() => setLogicExample(null)}
                  sx={{ color: TRIAGE_TEXT_SECONDARY }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box
                sx={{
                  p: 1.25,
                  overflowY: "auto",
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 0.75,
                    mb: 1,
                  }}
                >
                  <Box>
                    <Box sx={{ fontWeight: 900 }}>Assigned player</Box>
                    <Box sx={{ opacity: 0.78 }}>
                      p{logicExample.assignedPlayerId} • Team{" "}
                      {logicExample.team} • {logicExample.assignmentKind}
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ fontWeight: 900 }}>Reason</Box>
                    <Box sx={{ opacity: 0.78 }}>
                      {logicExample.reasonLabel}
                      {logicExample.reasonText
                        ? ` • ${logicExample.reasonText}`
                        : ""}
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ fontWeight: 900 }}>Score</Box>
                    <Box sx={{ opacity: 0.78 }}>
                      {logicExample.scoreBefore
                        ? `${logicExample.scoreBefore[0]}-${logicExample.scoreBefore[1]}`
                        : "?"}
                      {" -> "}
                      {logicExample.scoreAfter
                        ? `${logicExample.scoreAfter[0]}-${logicExample.scoreAfter[1]}`
                        : "?"}
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ fontWeight: 900 }}>Shots</Box>
                    <Box sx={{ opacity: 0.78 }}>
                      Focus{" "}
                      {logicExample.focusShotNumber == null
                        ? "none"
                        : logicExample.focusShotNumber}
                      {" • "}Terminal{" "}
                      {logicExample.terminalShotNumber == null
                        ? "?"
                        : logicExample.terminalShotNumber}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ fontWeight: 900, mb: 0.45 }}>Rationale</Box>
                {logicExample.rationale.length ? (
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.25, mb: 1, color: TRIAGE_TEXT_SECONDARY }}
                  >
                    {logicExample.rationale.map((line, idx) => (
                      <li key={`${logicExample.rallyIndex}-rationale-${idx}`}>
                        {line}
                      </li>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ mb: 1, color: TRIAGE_TEXT_SECONDARY }}>
                    No rationale lines were provided by the assignment builder.
                  </Box>
                )}

                {logicExample.supportSignals.length ? (
                  <>
                    <Box sx={{ fontWeight: 900, mb: 0.45 }}>
                      Support signals
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {logicExample.supportSignals.map((s, idx) => (
                        <Box
                          key={`${s.key}-${idx}`}
                          sx={{
                            px: 0.75,
                            py: 0.35,
                            borderRadius: 1,
                            border: `1px solid ${TRIAGE_BORDER}`,
                            background: TRIAGE_CONTROL_BG,
                            color: TRIAGE_TEXT_SECONDARY,
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          {s.key}: {s.value}
                        </Box>
                      ))}
                    </Box>
                  </>
                ) : null}
              </Box>
            </Box>
          ) : null}

          <Box
            onPointerDown={onResizePointerDown}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            sx={{
              position: "absolute",
              left: 0,
              bottom: 0,
              width: 18,
              height: 18,
              cursor: "nwse-resize",
              touchAction: "none",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 4,
                bottom: 4,
                width: 10,
                height: 10,
                borderRight: `2px solid ${TRIAGE_HANDLE}`,
                borderBottom: `2px solid ${TRIAGE_HANDLE}`,
              },
            }}
          />

          {/* {showAiGuide && aiGuideGeometry && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 5,
              }}
            >
              <svg
                width="100%"
                height="100%"
                style={{ display: "block", overflow: "visible" }}
              >
                <defs>
                  <marker
                    id="triageAiGuideArrow"
                    markerWidth="8"
                    markerHeight="8"
                    refX="6.5"
                    refY="4"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path
                      d="M0,0 L8,4 L0,8 z"
                      fill={AI_GUIDE_COLOR}
                      stroke={AI_GUIDE_COLOR}
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </marker>
                </defs>

                {(() => {
                  const { startX, startY, endX, endY } = aiGuideGeometry;

                  return (
                    <line
                      x1={startX}
                      y1={startY}
                      x2={endX}
                      y2={endY}
                      stroke={AI_GUIDE_COLOR}
                      strokeWidth="3"
                      strokeLinecap="round"
                      markerEnd="url(#triageAiGuideArrow)"
                    />
                  );
                })()}
              </svg>
            </Box>
          )} */}
        </Box>
      </Box>
    </>
  );
}
